import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClbTag } from '@celebration/react/ClbTag/ClbTag';
import { useTranslation } from 'react-i18next';
import Auth from '../../../../app/contexts/auth';
import ITableHead from '../../../../common/components/table/interfaces/ITableHead';
import Table from '../../../../common/components/table';
import IUsersTable from '../../interfaces/IUsersTable';
import IActionButtonIcon from '../../../../common/components/table/interfaces/IActionButtonIcon';
import {
    SULAHUB_ROLES,
    SULAHUB_USERS_TYPES,
} from '../../../../common/constants/roles';
import UsersManagementService from '../../services/users-management.service';
import IUsersDataTable from '../../interfaces/IUsersDataTable';
import UsersConstants from '../../constants';
import IPagination from '../../../../common/interfaces/IPagination';
import { IUsersResponse } from '../../interfaces/IUsersResponse';
import IUsersFiltersModel from '../../interfaces/IUsersFiltersModel';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import UsersFilters from '../users-filters';
import sortDataBy from '../../../../utils/sort-data-utils';
import { ISelectData } from '../../../../common/components/select-autocomplete/interfaces/ISelectData';
import isAmbevUser from './users-table.utils';

function UsersTable({
    searchTerm,
    showFilters,
    toggleShowFilters,
    setIsUserEditFormOpened,
    setEditUserId,
    setGetUsersData,
    getUsersData,
    setDeactivateUserInfo,
    setDeactivateUserModalOpened,
}: Readonly<IUsersTable>) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const { isAuthorized, isThirdUser } = Auth.useAuth();

    const [usersDataTable, setUsersDataTable] = useState<
        IPagination<IUsersDataTable>
    >(UsersConstants.DEFAULT_DATA);

    const sortBy = useRef<string>();
    const filters = useRef<IUsersFiltersModel>({} as IUsersFiltersModel);
    const [fetchingData, setFetchingData] = useState(false);
    const page = useRef(0);

    const [selectedCarriers, setSelectedCarriers] = useState<ISelectData[]>([]);

    const getUserStatusTag = useCallback(
        (id: string, status: boolean) => {
            if (status) {
                return (
                    <ClbTag id={id} type="positive">
                        {t('Ativo')}
                    </ClbTag>
                );
            }
            return (
                <ClbTag id={id} type="negative">
                    {t('Inativo')}
                </ClbTag>
            );
        },
        [t],
    );

    const mapUsersToDataTable = useCallback(
        (data: IUsersResponse[]) => {
            return data.map(user => {
                const filteredRoles = user.roles.filter(
                    x =>
                        x.name !== 'default' &&
                        x.name !==
                            SULAHUB_ROLES.SYSTEM_PROFILES.ONTIMELEILAO_USER &&
                        x.name !==
                            SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
                );

                let userRoleDisplayed: string;
                if (filteredRoles.length === 0) {
                    userRoleDisplayed = '';
                } else {
                    userRoleDisplayed =
                        SULAHUB_USERS_TYPES.find(
                            r => r.value === filteredRoles[0].name,
                        )?.label ?? '';
                }

                return {
                    id: user.id,
                    name: user.name,
                    email: user.email,
                    carrierName: user.carrierName,
                    status: getUserStatusTag(user.id, user.active),
                    statusDate:
                        new Date(user.updatedAt).toLocaleDateString() ?? '',
                    role: userRoleDisplayed,
                    active: user.active,
                    isAmbevUser: !isAmbevUser(user),
                } as IUsersDataTable;
            });
        },
        [getUserStatusTag],
    );

    const dataTablePaginated = useCallback(
        (
            userData: IPagination<IUsersResponse>,
            newDataTable?: IUsersDataTable[],
        ) => {
            return {
                data: newDataTable ?? mapUsersToDataTable(userData.data),
                currentPage: userData.currentPage,
                totalItems: userData.totalItems,
                totalPages: userData.totalPages,
            } as IPagination<IUsersDataTable>;
        },
        [mapUsersToDataTable],
    );

    const fetchData = useCallback(
        async (backFirstPage?: boolean) => {
            const userData = await UsersManagementService.getUsersList(
                backFirstPage ? 1 : page.current,
                UsersConstants.PAGE_SIZE,
                sortBy.current ?? '',
                filters.current,
            ).catch((error: Error) => {
                setToast('negative', 'Erro', error.message);
            });

            if (userData) {
                page.current = userData.currentPage;

                if (userData.currentPage === 1) {
                    setUsersDataTable(dataTablePaginated(userData));
                } else if (usersDataTable) {
                    const currentData: IUsersDataTable[] = [
                        ...usersDataTable.data,
                    ];
                    const newData = mapUsersToDataTable(userData.data);
                    const newUsersDataTable = currentData.concat(newData);
                    setUsersDataTable(
                        dataTablePaginated(userData, newUsersDataTable),
                    );
                }
            }
            setGetUsersData(false);
            setFetchingData(false);
        },
        [
            dataTablePaginated,
            mapUsersToDataTable,
            setGetUsersData,
            setToast,
            usersDataTable,
        ],
    );

    const onApplyFilters = useCallback(
        (filtersModel: IUsersFiltersModel) => {
            filters.current = { ...filtersModel, description: searchTerm };
            toggleShowFilters();
            fetchData(true);
        },
        [searchTerm, toggleShowFilters, fetchData],
    );

    useEffect(() => {
        if (page.current === 0 || getUsersData) {
            page.current = 1;
            setFetchingData(true);
            fetchData();
        }
    }, [fetchData, setFetchingData, page, getUsersData]);

    useEffect(() => {
        if (
            searchTerm !== undefined &&
            searchTerm !== filters.current?.description
        ) {
            const delayDebounceFn = setTimeout(() => {
                filters.current = {
                    ...filters.current,
                    description: searchTerm,
                };
                fetchData(true);
            }, 300);

            return () => clearTimeout(delayDebounceFn);
        }

        return () => {};
    }, [searchTerm, fetchData]);

    const loadMoreButtonHandler = useCallback(async (): Promise<void> => {
        page.current += 1;
        await fetchData();
    }, [fetchData, page]);

    const sortData = useCallback(
        (event: any, field: string) => {
            sortBy.current = sortDataBy(event, field);
            fetchData(true);
        },
        [fetchData],
    );

    const getTableHeaders = (): ITableHead[] => {
        let headers: ITableHead[] = [
            { id: 'name', label: t('Nome'), field: 'name' },
            { id: 'email', label: t('Email'), field: 'email' },
            {
                id: 'role',
                label: t('TipoUsuario'),
                field: 'role',
                sortable: true,
                sortChange: e => sortData(e, 'role'),
            },
            {
                id: 'carrierName',
                label: t('Transportadora'),
                field: 'carrierName',
            },
            {
                id: 'status',
                label: t('Status'),
                field: 'status',
                alignContent: 'center',
                sortable: true,
                sortChange: e => sortData(e, 'status'),
            },
            {
                id: 'statusDate',
                label: t('DataModificacao'),
                field: 'statusDate',
            },
        ];

        if (isThirdUser()) {
            headers = headers.filter(h => h.label !== t('Transportadora'));
        }

        return headers;
    };

    const userEditButtonHandler = useCallback(
        async ({ id }: IUsersDataTable) => {
            setEditUserId(id);
            setIsUserEditFormOpened(true);
        },
        [setEditUserId, setIsUserEditFormOpened],
    );

    const userDeactivateButtonHandler = useCallback(
        (user: IUsersDataTable) => {
            setDeactivateUserInfo(user);
            setDeactivateUserModalOpened(true);
            fetchData(true);
        },
        [setDeactivateUserInfo, setDeactivateUserModalOpened, fetchData],
    );

    const actionButtonIcons: IActionButtonIcon[] = useMemo(() => {
        const actionsOptions: IActionButtonIcon[] = [];

        if (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ])
        ) {
            actionsOptions.push({
                icon: 'Edit',
                tooltipLabel: t('Editar'),
                callback: user => userEditButtonHandler(user),
            });

            actionsOptions.push({
                icon: 'Delete',
                tooltipLabel: t('Deactivate'),
                callback: user => userDeactivateButtonHandler(user),
            });
        }

        return actionsOptions;
    }, [isAuthorized, userEditButtonHandler, userDeactivateButtonHandler, t]);

    return (
        <>
            <Table
                content={usersDataTable}
                headers={getTableHeaders()}
                fetchingData={fetchingData}
                loadMoreButtonCallback={loadMoreButtonHandler}
                emptyTableDescription={t('NenhumUsuarioExibir')}
                hideToolbarTable
                itemButtonActions={actionButtonIcons}
            />

            {showFilters && (
                <UsersFilters
                    currentFilters={filters.current}
                    isShowingFilters={showFilters}
                    onApplyFilter={onApplyFilters}
                    toggleShowFilters={toggleShowFilters}
                    selectedCarriers={selectedCarriers}
                    setSelectedCarriers={setSelectedCarriers}
                    key="users-filters"
                />
            )}
        </>
    );
}

export default UsersTable;
